import { Injectable } from '@angular/core';
import { ReportSettings } from '@models/reportSettings.model';
import { ColumnHeaders } from '@shared/_constants';
import { LicensingCountry } from '../models/licensingCountry.model';
import { ReportTemplates } from '../mystudies/_study_constants';

@Injectable({
  providedIn: 'root',
})
export class ReportsService {
  public getReportSettings(type: ReportTemplates) {
    switch (type) {
      case ReportTemplates.SearchStrategy: {
        return this.searchStrategy;
      }
      case ReportTemplates.KeywordScreening: {
        return this.keywordScreening;
      }
      case ReportTemplates.BDMatrix: {
        return this.bdMatrix;
      }
      case ReportTemplates.InternetReview: {
        return this.internetReview;
      }
      case ReportTemplates.FinancialAdjustments: {
        return this.financialAdjustments;
      }
      case ReportTemplates.AcceptedCompanies: {
        return this.acceptedCompanies;
      }
      case ReportTemplates.AcceptedRejectedCompanies: {
        return this.acceptedRejectedCompanies;
      }
      case ReportTemplates.EliminationChart: {
        return this.EliminationChart;
      }
      case ReportTemplates.EngagementCodeReport: {
        return this.engagementCodeReport;
      }
      case ReportTemplates.FinancialAutoAdjustments: {
        return this.financialAdjustments;
      }
      case ReportTemplates.DetailedPLISummary: {
        return this.detailedPLISummaryReport;
      }
      case ReportTemplates.SearchWriteUp: {
        return this.searchWriteUpReport;
      }
      case ReportTemplates.StudyByDate: {
        return this.studyByDate;
      }
      case ReportTemplates.ContemporaryFinancialSummaryReport: {
        return this.contemporaryFinancialSummaryReport;
      }
    }
    return this.searchStrategy;
  }

  public get searchStrategy(): ReportSettings {
    const settings = new ReportSettings();
    settings.defaultColumns = [ColumnHeaders.databaseInformation, ColumnHeaders.filters, ColumnHeaders.booleanLogic, ColumnHeaders.numberRetained];
    settings.optionalColumns = [
      {
        label: ColumnHeaders.companyName,
        data: 0,
      },
      {
        label: ColumnHeaders.bvdId,
        data: 6,
      },
      {
        label: ColumnHeaders.country,
        data: 1,
      },
      {
        label: ColumnHeaders.tradeDescriptionEN,
        data: 2,
      },
      {
        label: ColumnHeaders.tradeDescriptionNL,
        data: 3,
      },
      {
        label: ColumnHeaders.tradeDescriptionTranslated,
        data: 4,
      },
      {
        label: ColumnHeaders.fullOverview,
        data: 5,
      },
    ];
    return settings;
  }

  public get studyByDate(): ReportSettings {
    const settings = new ReportSettings();
    settings.defaultColumns = [
      ColumnHeaders.StudyID,
      ColumnHeaders.StudyName,
      ColumnHeaders.CreatedOn,
      ColumnHeaders.PreparerName,
      ColumnHeaders.PreparerEmail,
      ColumnHeaders.LicensingCountry,
      ColumnHeaders.ProjectOfficeCountry,
      ColumnHeaders.DatabaseNames,
      ColumnHeaders.IsDemoStudy,
      ColumnHeaders.StudyStatus,
    ];
    settings.optionalColumns = [
      {
        label: ColumnHeaders.IsDemoStudy,
        data: 0,
      },
    ];
    return settings;
  }

  public get keywordScreening(): ReportSettings {
    const settings = new ReportSettings();
    settings.defaultColumns = [ColumnHeaders.includeExcludeList, ColumnHeaders.keywordOccurrence, ColumnHeaders.numberRetained];
    settings.optionalColumns = [
      {
        label: ColumnHeaders.companyName,
        data: 0,
      },
      {
        label: ColumnHeaders.country,
        data: 1,
      },
      {
        label: ColumnHeaders.tradeDescriptionEN,
        data: 2,
      },
      {
        label: ColumnHeaders.tradeDescriptionNL,
        data: 3,
      },
      {
        label: ColumnHeaders.fullOverview,
        data: 4,
      },
      {
        label: ColumnHeaders.bvdId,
        data: 5,
      },
      {
        label: ColumnHeaders.industryCode,
        data: 6,
      },
      {
        label: ColumnHeaders.tradeDescriptionTranslated,
        data: 7,
      },
    ];
    return settings;
  }

  public get bdMatrix(): ReportSettings {
    const settings = new ReportSettings();
    settings.defaultColumns = [ColumnHeaders.companyName, ColumnHeaders.country, ColumnHeaders.eliminationCriteria, ColumnHeaders.rejectReason];

    settings.optionalColumns = [
      {
        label: ColumnHeaders.tradeDescriptionEN,
        data: 6,
      },
      {
        label: ColumnHeaders.tradeDescriptionNL,
        data: 7,
      },
      {
        label: ColumnHeaders.tradeDescriptionTranslated,
        data: 8,
      },
      {
        label: ColumnHeaders.fullOverview,
        data: 9,
      },
      {
        label: ColumnHeaders.bvdId,
        data: 10,
      },
      {
        label: ColumnHeaders.website,
        data: 11,
      },
      {
        label: ColumnHeaders.subset,
        data: 12,
      },
      {
        label: ColumnHeaders.internalNotes,
        data: 13,
      },
    ];
    return settings;
  }

  public get internetReview(): any {
    const settings = new ReportSettings();
    settings.defaultColumns = [
      ColumnHeaders.companyName,
      ColumnHeaders.websiteVendorProvided,
      ColumnHeaders.websiteUserProvided,
      ColumnHeaders.status,
      ColumnHeaders.numberAcceptReject,
      ColumnHeaders.eliminationCriteria,
      ColumnHeaders.rejectReason,
    ];
    settings.optionalColumns = [
      {
        label: ColumnHeaders.country,
        data: 4,
      },
      {
        label: ColumnHeaders.bvdId,
        data: 3,
      },
      {
        label: ColumnHeaders.subset,
        data: 7,
      },
      {
        label: ColumnHeaders.internalNotes,
        data: 8,
      },
      {
        label: ColumnHeaders.tradeDescriptionEN,
        data: 9,
      },
      {
        label: ColumnHeaders.tradeDescriptionNL,
        data: 10,
      },
      {
        label: ColumnHeaders.tradeDescriptionTranslated,
        data: 11,
      },
      {
        label: ColumnHeaders.fullOverview,
        data: 12,
      },
    ];
    return settings;
  }

  public get financialAdjustments(): any {
    const settings = new ReportSettings();
    settings.defaultColumns = [
      ColumnHeaders.companyName,
      ColumnHeaders.numberOfCompanies,
      ColumnHeaders.iqr,
      ColumnHeaders.pliSettings,
      ColumnHeaders.periodOfStudy,
      ColumnHeaders.currencyInformation,
      ColumnHeaders.country,
      ColumnHeaders.bvdId,
    ];
    settings.optionalColumns = [
      {
        label: ColumnHeaders.industryCode,
        data: 0,
      },
    ];
    return settings;
  }

  public get acceptedCompanies(): any {
    const settings = new ReportSettings();
    settings.defaultColumns = [
      ColumnHeaders.companyName,
      ColumnHeaders.country,
      ColumnHeaders.tradeDescriptionEN,
      ColumnHeaders.tradeDescriptionNL,
      ColumnHeaders.tradeDescriptionTranslated,
      ColumnHeaders.fullOverview,
      ColumnHeaders.database,
      ColumnHeaders.databaseVersion,
      ColumnHeaders.industryCode,
    ];
    // settings.optionalColumns = [
    //  {
    //    label: ColumnHeaders.bvdId,
    //    data: 5
    //  }, {
    //    label: ColumnHeaders.industryCode,
    //    data: 11
    //  }
    // ];
    return settings;
  }

  public get acceptedRejectedCompanies(): any {
    const settings = new ReportSettings();
    settings.defaultColumns = [
      ColumnHeaders.companyName,
      ColumnHeaders.country,
      ColumnHeaders.tradeDescriptionEN,
      ColumnHeaders.tradeDescriptionNL,
      ColumnHeaders.tradeDescriptionTranslated,
      ColumnHeaders.fullOverview,
      ColumnHeaders.database,
      ColumnHeaders.databaseVersion,
      ColumnHeaders.subset,
      ColumnHeaders.reason,
      ColumnHeaders.source,
      ColumnHeaders.exclusionReasons,
    ];
    // ,
    // settings.optionalColumns = [
    //  {
    //    label: ColumnHeaders.bvdId,
    //    data: 5
    //  }, {
    //    label: ColumnHeaders.industryCode,
    //    data: 11
    //  }
    // ];
    return settings;
  }

  public get EliminationChart(): any {
    const settings = new ReportSettings();
    settings.defaultColumns = [
      ColumnHeaders.companyName,
      ColumnHeaders.bvdId,
      ColumnHeaders.country,
      ColumnHeaders.websiteUserProvided,
      ColumnHeaders.websiteVendorProvided,
      ColumnHeaders.tradeDescriptionEN,
      ColumnHeaders.tradeDescriptionNL,
      ColumnHeaders.tradeDescriptionTranslated,
      ColumnHeaders.fullOverview,
      ColumnHeaders.eliminationCriteria,
      ColumnHeaders.subset,
      ColumnHeaders.reason,
      ColumnHeaders.source,
      ColumnHeaders.numberOfCompaniesAccepted,
      ColumnHeaders.numberOfCompaniesRejected,
    ];

    settings.optionalColumns = [
      {
        label: ColumnHeaders.internalNotes,
        data: 13,
      },
      {
        label: ColumnHeaders.companyAddress,
        data: 14,
      },
    ];
    return settings;
  }

  public get engagementCodeReport(): any {
    const settings = new ReportSettings();
    settings.defaultColumns = [];
    settings.optionalColumns = [];
    return settings;
  }
  public get searchWriteUpReport(): any {
    const settings = new ReportSettings();
    settings.defaultColumns = [];
    settings.optionalColumns = [];
    return settings;
  }
  public get contemporaryFinancialSummaryReport(): any {
    const settings = new ReportSettings();
    settings.defaultColumns = [];
    settings.optionalColumns = [];
    return settings;
  }

  public get detailedPLISummaryReport(): any {
    const settings = new ReportSettings();
    settings.defaultColumns = [];
    settings.optionalColumns = [];
    return settings;
  }
}
